/**
 * Badge - Status Indicator Component
 *
 * Small label for displaying status, counts, or categories.
 * Renders as Box (span) with CVA variants.
 *
 * @example
 * <Badge variant="success">Active</Badge>
 * <Badge variant="warning">Pending</Badge>
 * <Badge variant="error">Rejected</Badge>
 */
import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

const badgeVariants = cva(
  "inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full",
  {
    variants: {
      variant: {
        default: "bg-gray-100 text-gray-700",
        success: "bg-green-100 text-green-700",
        warning: "bg-yellow-100 text-yellow-700",
        error: "bg-red-100 text-red-700",
        info: "bg-blue-100 text-blue-700",
      },
      size: {
        sm: "text-xs px-1.5 py-0.5",
        md: "text-xs px-2 py-0.5",
        lg: "text-sm px-2.5 py-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface BadgeProps
  extends BaseComponentProps, VariantProps<typeof badgeVariants> {}

/**
 * Badge component
 * Renders as a Box (span) with badge styling
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { variant = "default", size = "md", className, children, ...props },
    ref,
  ) => {
    return (
      <Box
        as="span"
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

Badge.displayName = "Badge";
